import { User } from 'oidc-client-ts'
import { Fragment, PropsWithChildren, useCallback, useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { Constants } from '../helpers/Constants'
import { AuthContext, UserAuthContext, UserContext, getUserContext } from './authContext'
import {
  PostLogoutRedirectUri,
  RedirectUri,
  SilentRedirectUri,
  USER_MANAGER,
  USER_MANAGER_PRIMARY,
} from './userManager'

export const MandatorySignIn = true
export const Home = '/'
export const ForceLogout = (user: UserContext) => {
  return user.isSupplier
}

export function AuthProvider(props: PropsWithChildren) {
  const initialized = useRef(false)
  const [authData, setAuthData] = useState<UserAuthContext>({
    manager: USER_MANAGER,
    user: undefined,
    primaryUser: undefined,
    impersonate: async () => undefined,
    signout: () => undefined,
  })
  const history = useHistory()

  const impersonate = useCallback(async (tenantId: string | undefined) => {
    try {
      let currentOidcUser = await USER_MANAGER.getUser()
      if (!currentOidcUser) return
      const currentUser = getUserContext(currentOidcUser)
      let primaryOidcUser = currentUser.isImpersonated
        ? await USER_MANAGER_PRIMARY.signinSilent()
        : await USER_MANAGER.signinSilent()
      if (!primaryOidcUser) return
      if (!tenantId) {
        currentOidcUser = primaryOidcUser
        console.log({ currentOidcUser })
        primaryOidcUser = null
        await USER_MANAGER.storeUser(currentOidcUser)
        await USER_MANAGER_PRIMARY.storeUser(null)
      } else {
        const params = new URLSearchParams({
          grant_type: 'urn:ietf:params:oauth:grant-type:token-exchange',
          client_id: 'Interfleet',
          subject_token: primaryOidcUser.access_token,
          subject_token_type: 'urn:ietf:params:oauth:token-type:access_token',
          tenant_id: tenantId ?? '',
          scope: primaryOidcUser.scope ?? '',
        })
        const response = await fetch(`${Constants.stsAuthority}connect/token`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: params.toString(),
        })

        if (!response.ok) return

        const tokenData = await response.json()

        const jwtPayload = JSON.parse(atob(tokenData.access_token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')))
        const exchangedUser = new User({
          access_token: tokenData.access_token,
          refresh_token: tokenData.refresh_token,
          token_type: tokenData.token_type,
          scope: tokenData.scope,
          expires_at: Math.floor(Date.now() / 1000) + tokenData.expires_in,
          profile: jwtPayload,
        })

        await USER_MANAGER_PRIMARY.storeUser(primaryOidcUser)
        await USER_MANAGER.storeUser(exchangedUser)
        currentOidcUser = exchangedUser
      }
      setAuthData((current) => ({
        ...current,
        user: currentOidcUser ? getUserContext(currentOidcUser) : null,
        primaryUser: primaryOidcUser ? getUserContext(primaryOidcUser) : null,
      }))
    } catch (e) {
      console.error(e)
    }
  }, [])

  const processPrimaryOidcUser = useCallback((user?: UserContext | null): Promise<UserContext | null | undefined> => {
    if (user === undefined) return Promise.resolve(undefined)
    if (user === null || !user.isImpersonated) return Promise.resolve(null)
    return USER_MANAGER_PRIMARY.signinSilent()
      .then((u) => (u ? getUserContext(u) : null))
      .catch(() => null)
  }, [])

  const signout = useCallback(() => {
    USER_MANAGER_PRIMARY.storeUser(null)
      .finally(() => USER_MANAGER.signoutRedirect())
      .then(console.debug)
  }, [])

  const processOidcUser = useCallback(
    (oidcUser: User | null | undefined) => {
      if (oidcUser === undefined) return
      const user = oidcUser ? getUserContext(oidcUser) : null
      if (user && ForceLogout(user)) {
        signout()
      } else if (!user && MandatorySignIn) {
        USER_MANAGER.signinRedirect()
      } else {
        processPrimaryOidcUser(user).then((primaryUser) => {
          const redirect = window.location.pathname.startsWith(RedirectUri)
          const redirectUri = localStorage.getItem('login-redirect') ?? Home
          localStorage.removeItem('login-redirect')
          if (redirect) history.push(redirectUri)
          setAuthData((current) => ({ ...current, user, primaryUser, impersonate, signout }))
        })
      }
    },
    [history, impersonate, processPrimaryOidcUser, signout]
  )

  useEffect(() => {
    if (initialized.current) return
    initialized.current = true
    const path = window.location.pathname
    let userPromise: Promise<User | null | undefined> = Promise.resolve(null)
    if (path.startsWith(RedirectUri)) {
      userPromise = USER_MANAGER.signinCallback().then((user) => user ?? null)
    } else if (path.startsWith(PostLogoutRedirectUri)) {
      userPromise = USER_MANAGER.signoutCallback().then(() => null)
    } else if (path.startsWith(SilentRedirectUri)) {
      userPromise = USER_MANAGER.signinCallback().finally(() => undefined)
    } else {
      userPromise = USER_MANAGER.signinSilent().catch(() => {
        localStorage.setItem('login-redirect', location.pathname + location.search)
        return null
      })
    }

    userPromise.then(processOidcUser).catch(() => processOidcUser(null))
  }, [processOidcUser])

  return (
    <AuthContext.Provider value={authData}>
      <Fragment key={authData.user?.tenant ?? ''}>{authData.user !== undefined ? props.children : null}</Fragment>
    </AuthContext.Provider>
  )
}
